import { create } from "zustand";
import { documentsApi, expertisesApi } from "@/api";
import useExpertiseStore from "./useExpertiseStore";

const useDocumentStore = create((set, get) => ({
  documents: [],
  uploading: {},
  analyzing: {},
  error: null,

  addDocument: async (expertiseId, label) => {
    try {
      set({ error: null });
      const doc = await documentsApi.create(expertiseId, label);
      set((state) => ({ documents: [...state.documents, doc] }));
      return doc;
    } catch (err) {
      set({ error: err.message });
      throw err;
    }
  },

  removeDocument: async (docId) => {
    try {
      set({ error: null });
      await documentsApi.remove(docId);
      set((state) => ({
        documents: state.documents.filter((d) => d.id !== docId),
      }));
    } catch (err) {
      set({ error: err.message });
      throw err;
    }
  },

  fetchDocuments: async (expertiseId) => {
    try {
      set({ error: null });
      const documents = await documentsApi.list(expertiseId);
      set({ documents });
    } catch (err) {
      set({ error: err.message });
    }
  },

  updateStatus: async (docId, statut) => {
    try {
      set({ error: null });
      const updated = await documentsApi.updateStatus(docId, statut);
      set((state) => ({
        documents: state.documents.map((d) => (d.id === docId ? updated : d)),
      }));
    } catch (err) {
      set({ error: err.message });
    }
  },

  uploadFile: async (expertiseId, typeDocument, file) => {
    try {
      set((state) => ({
        uploading: { ...state.uploading, [typeDocument]: true },
        error: null,
      }));
      const updated = await documentsApi.upload(expertiseId, typeDocument, file);
      set((state) => ({
        documents: state.documents.map((d) =>
          d.type_document === typeDocument ? updated : d
        ),
        uploading: { ...state.uploading, [typeDocument]: false },
      }));
      return updated;
    } catch (err) {
      set((state) => ({
        uploading: { ...state.uploading, [typeDocument]: false },
        error: err.message,
      }));
      throw err;
    }
  },

  deleteFile: async (docId) => {
    try {
      set({ error: null });
      await documentsApi.deleteFile(docId);
      set((state) => ({
        documents: state.documents.map((d) =>
          d.id === docId
            ? {
                ...d,
                s3_key: null,
                filename_original: null,
                file_url: null,
                analyse_statut: "none",
                analyse_resume: null,
                analyse_donnees_extraites: null,
              }
            : d
        ),
      }));
    } catch (err) {
      set({ error: err.message });
    }
  },

  triggerAnalysis: async (docId) => {
    try {
      set((state) => ({
        analyzing: { ...state.analyzing, [docId]: true },
        error: null,
      }));
      await documentsApi.analyze(docId);
      // Mettre à jour le statut localement
      set((state) => ({
        documents: state.documents.map((d) =>
          d.id === docId ? { ...d, analyse_statut: "pending" } : d
        ),
      }));
      // Démarrer le polling
      get().pollAnalysis(docId);
    } catch (err) {
      set((state) => ({
        analyzing: { ...state.analyzing, [docId]: false },
        error: err.message,
      }));
    }
  },

  pollAnalysis: async (docId) => {
    const poll = async () => {
      try {
        const analysis = await documentsApi.getAnalysis(docId);
        if (analysis.analyse_statut === "pending") {
          setTimeout(poll, 2000);
          return;
        }
        // Analyse terminée (done ou error)
        set((state) => ({
          documents: state.documents.map((d) =>
            d.id === docId ? { ...d, ...analysis } : d
          ),
          analyzing: { ...state.analyzing, [docId]: false },
        }));
      } catch {
        set((state) => ({
          analyzing: { ...state.analyzing, [docId]: false },
        }));
      }
    };
    poll();
  },

  validateAnalysis: async (docId, acceptedFields) => {
    try {
      set({ error: null });
      const response = await documentsApi.validate(docId, acceptedFields);
      // Backend retourne { document, injected_fields } depuis Task 3.
      const fresh = response?.document ?? response;
      const injectedFields = response?.injected_fields ?? [];

      // 1. Met à jour le document dans le store docs.
      set((state) => ({
        documents: state.documents.map((d) => (d.id === docId ? fresh : d)),
      }));

      // 2. Si des champs ont été injectés, rafraîchir l'expertise
      //    pour que les valeurs apparaissent immédiatement dans le formulaire.
      if (injectedFields.length > 0 && fresh?.expertise_id) {
        try {
          const freshExpertise = await expertisesApi.getComplete(
            fresh.expertise_id
          );
          useExpertiseStore.getState().setExpertiseFromServer(freshExpertise);
        } catch {
          // L'injection a réussi côté backend ; si le refresh échoue,
          // on laisse le store tel quel — l'utilisateur peut recharger.
        }
      }

      return response;
    } catch (err) {
      set({ error: err.message });
      throw err;
    }
  },
}));

export default useDocumentStore;
